import { type Ref, ref, watch } from 'vue'

import { useEventListener } from '@vueuse/core'

interface UsePanelDragOptions {
  /**
   * Drag is only live while this is true — i.e. the sheet is open/expanded and
   * we're on a touch/mobile layout (the handle is `md:hidden` on desktop, so
   * pointer events never fire there, but we gate here too for safety).
   */
  enabled: Ref<boolean>
  /**
   * The sheet's resting size as a two-level flag: `false` = its default height,
   * `true` = its full height. Two-way — the drag flips it on release, and the
   * caller binds it to the resting-height class, and this write is what sets the
   * user's shared level preference. At rest the class owns the height; this
   * composable only overrides it (in px) while a finger is down.
   */
  expanded: Ref<boolean>
  /**
   * Called when a downward drag should close the sheet. The caller maps this to
   * the existing URL navigation (`navigateToHome()`/collapse for the pill,
   * `emit('close')` for overlays) so open/close stays the URL's job.
   */
  onDismiss: () => void
  /**
   * Two-stage collapse. When true (the article + list sheets), a downward drag
   * from full height only steps back to the default height — never closes in one
   * gesture — and only a drag from the default height closes. When false (the
   * `?metal=` quick view), a downward drag from any height closes outright.
   */
  twoStage?: boolean
  /**
   * Lowest height (px) the drag may shrink the panel to while the finger is
   * down — defaults to 0 so the sheet can follow all the way out. The pill
   * passes its collapsed height so it shrinks straight into the collapsed bar.
   */
  minHeight?: () => number
}

// Release thresholds, as a fraction of the height the gesture started from; a
// fast flick (below) always wins regardless of distance.
const DISMISS_FRACTION = 0.4 // drag down this far from the default height → close
const STEP_FRACTION = 0.2 // drag this far → step one level (expand ↑, or collapse to default ↓)
// Two-stage only: releasing below this fraction of the full height closes the
// sheet outright rather than stepping to the default — a deliberate long drag to
// the bottom, judged by distance (not velocity), so a slow full drag still closes.
const DISMISS_LEVEL = 0.5

/**
 * Pointer-driven bottom-sheet drag, built on raw Pointer Events (zero new deps).
 *
 * Bind it to a grab `handle` sitting over a `panel`. The sheet rests at one of two
 * caller-owned levels — default or full height (`expanded`). Dragging the handle
 * overrides the height in px to track the finger; on release it snaps to a level
 * (flipping `expanded`) or calls `onDismiss`, per the two-stage rules above.
 *
 * Returns a transient `height` (px, or `null` at rest so the caller's height class
 * owns the sizing and animates the snap) and a `dragging` flag the panel uses to
 * drop its height transition mid-drag. The gesture lives on the handle only, so
 * the Lenis scroll body is untouched.
 */
export function usePanelDrag(
  panel: Ref<HTMLElement | null>,
  handle: Ref<HTMLElement | null>,
  options: UsePanelDragOptions,
) {
  const { enabled, expanded, onDismiss, twoStage = false, minHeight } = options

  // px override applied only while dragging; null at rest → the caller's class
  // (default vs full height, keyed off `expanded`) owns the height.
  const height = ref<number | null>(null)
  const dragging = ref(false)

  let maxHeight = 0
  let startY = 0
  let startHeight = 0
  // Whether the gesture began from the full-height level — selects the two-stage
  // branch on release.
  let startedExpanded = false
  let pointerId = -1
  // Last move sample, for a release-velocity estimate (downward = positive).
  let lastY = 0
  let lastT = 0
  let velocity = 0

  // The tallest the panel may grow to: its own computed max-height (the sheets
  // set `calc(100dvh - …)`), resolved to px by the browser. Cached per drag in
  // onDown — it can't change mid-gesture, and reading getComputedStyle every
  // pointermove would thrash layout.
  function resolveMaxHeight(): number {
    const el = panel.value
    if (!el) return window.innerHeight
    const raw = getComputedStyle(el).maxHeight
    const px = parseFloat(raw)
    return raw !== 'none' && Number.isFinite(px) ? px : window.innerHeight
  }

  function onDown(event: PointerEvent) {
    if (!enabled.value || !panel.value) return
    dragging.value = true
    startY = event.clientY
    startHeight = panel.value.getBoundingClientRect().height
    startedExpanded = expanded.value
    maxHeight = resolveMaxHeight()
    lastY = startY
    lastT = event.timeStamp
    velocity = 0
    // eslint-disable-next-line prefer-destructuring
    pointerId = event.pointerId
    handle.value?.setPointerCapture(pointerId)
  }

  function onMove(event: PointerEvent) {
    if (!dragging.value) return
    const delta = event.clientY - startY // down = positive
    const min = minHeight?.() ?? 0
    height.value = Math.min(maxHeight, Math.max(min, startHeight - delta))
    const dt = event.timeStamp - lastT
    if (dt > 0) velocity = (event.clientY - lastY) / dt // px/ms, down = +
    lastY = event.clientY
    lastT = event.timeStamp
  }

  function endDrag() {
    if (!dragging.value) return
    dragging.value = false
    if (pointerId !== -1) {
      handle.value?.releasePointerCapture(pointerId)
      pointerId = -1
    }

    const current = height.value ?? startHeight
    const flickDown = velocity > 0.6 // px/ms ≈ 600px/s
    const flickUp = velocity < -0.4
    const shrunkBy = startHeight - current // > 0 = dragged down
    const grewBy = current - startHeight // > 0 = dragged up

    const dismiss = flickDown || shrunkBy > startHeight * DISMISS_FRACTION
    const steppedDown = flickDown || shrunkBy > startHeight * STEP_FRACTION
    const steppedUp = flickUp || grewBy > startHeight * STEP_FRACTION

    if (startedExpanded) {
      if (twoStage) {
        // Dragged deep past the default toward the bottom → close outright
        // (even slowly). A shorter drag or a fast flick only steps back to
        // the default — so a quick flick never closes in one go.
        if (current < maxHeight * DISMISS_LEVEL) {
          onDismiss()
          return
        }
        if (steppedDown) expanded.value = false
      } else if (dismiss) {
        // Quick view: a downward swipe closes it outright.
        onDismiss()
        return
      }
    } else if (dismiss) {
      // From the default height, a downward drag closes/collapses.
      onDismiss()
      return
    } else if (steppedUp) {
      expanded.value = true
    }

    // Release the finger-tracking override; the resting height class takes over
    // and the panel's own height transition animates the snap. (On dismiss we
    // return above and leave the dragged-down height for the caller's close
    // animation / the `enabled` watch to clear.)
    height.value = null
  }

  useEventListener(handle, 'pointerdown', onDown)
  useEventListener(handle, 'pointermove', onMove)
  useEventListener(handle, 'pointerup', endDrag)
  useEventListener(handle, 'pointercancel', endDrag)

  // When the sheet closes/collapses, drop any leftover drag override so it
  // reopens sized by its class. `watch` flushes before re-render, so the override
  // is gone in time for the close transition.
  watch(enabled, (on) => {
    if (!on) {
      dragging.value = false
      height.value = null
    }
  })

  return { height, dragging }
}
